import {
  DATA_ISSUE_FIX_MAX,
  dataIssuesResponseSchema,
  duplicateGroupSchema,
  duplicatesResponseSchema,
  fixDataIssuesResponseSchema,
  mergeLeadsResponseSchema,
  type DataIssue,
  type DataIssuesQuery,
  type DataIssuesResponse,
  type DismissDataIssueBody,
  type DismissDuplicateBody,
  type DuplicatesQuery,
  type DuplicatesResponse,
  type FixDataIssuesBody,
  type FixDataIssuesResponse,
  type MergeLeadsBody,
} from "@gioia365/contracts";
import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { z } from "zod";
import { apiRequest } from "../../api/http";
import { tabKey, type TabContext, type TabKey } from "../../session/tab-context";
import { leadCardsFilter, refreshAfterArchiveChange } from "../archive/archive-queries";

// Le letture e le scritture di «Pulizia dati» (piano M3 Task 18; rotte dei Task 16 e 17, D11 e D12): le coppie di doppioni,
// «Unisci in un solo Lead», «Non sono doppioni», le righe di «Telefoni ed email», «Correggi» e «Tieni così». Le chiavi stanno
// sotto `["scheda", tab, "leads", "cleanup"]`, così un cambio dei permessi o di «Chi vede i Lead», che rilegge tutte le
// letture dei Lead, rilegge anche queste; dopo viene la vista (la Vista Commerciale osservata, oppure `null`), perché coppie e
// righe stanno nel perimetro di chi guarda. Anche la coppia sola che «Unisci» apre dalla scheda del Lead (LEAD-28) sta lì sotto, così
// ogni scrittura di «Pulizia dati» la rilegge.

/** Le coppie di una pagina della scheda «Doppioni» («Mostra altri»). */
export const DUPLICATES_PAGE_SIZE = 20;
/** Le righe di una pagina della scheda «Telefoni ed email»: il massimo del server, così di solito sono tutte in vista. */
export const DATA_ISSUES_PAGE_SIZE = 100;

export const cleanupKeys = {
  all: (tab: TabKey) => ["scheda", tab, "leads", "cleanup"] as const,
  duplicates: (tab: TabKey, view: string | null, query: DuplicatesQuery) => ["scheda", tab, "leads", "cleanup", view, "duplicates", query] as const,
  duplicatesPages: (tab: TabKey, view: string | null) => ["scheda", tab, "leads", "cleanup", view, "duplicates", "pages"] as const,
  issues: (tab: TabKey, view: string | null, query: DataIssuesQuery) => ["scheda", tab, "leads", "cleanup", view, "issues", query] as const,
  issuesPages: (tab: TabKey, view: string | null) => ["scheda", tab, "leads", "cleanup", view, "issues", "pages"] as const,
  pair: (tab: TabKey, view: string | null, first: string, second: string) => ["scheda", tab, "leads", "cleanup", view, "pair", first, second] as const,
};

const pagePath = (path: string, query: { offset: number; limit: number }) => `${path}?offset=${query.offset}&limit=${query.limit}`;

/** La pagina successiva di una lettura a pagine, se ce n'è una. */
function nextOffset(last: { offset: number; limit: number; total: number }, rows: number): number | undefined {
  return last.offset + rows < last.total && rows > 0 ? last.offset + last.limit : undefined;
}

// I numeri del pulsante «Pulizia dati (N)» e delle schede: il server conta tutte le coppie e tutte le righe del perimetro,
// quindi restano freschi un minuto, come i numeri del menu delle Smart List (decisione P56). Tornare sull'elenco o sulla
// finestra del browser entro un minuto non li rilegge; ogni scrittura di «Pulizia dati» sì, e aprire la finestra «Pulizia
// dati» anche (`DataCleanupDialog`).
const COUNT_STALE_MS = 60_000;
// Le coppie e le righe della finestra: si rileggono ogni volta che la loro scheda si apre (`refetchOnMount: "always"`), così
// chi apre «Pulizia dati» lavora sui dati di adesso; dentro la finestra, tornare sul browser entro mezzo minuto non rilegge.
const PAGES_STALE_MS = 30_000;

/**
 * Una pagina delle coppie (`GET /api/leads/duplicates`). Con `limit: 1` dà soltanto il totale: il numero del pulsante
 * «Pulizia dati» e della scheda «Doppioni (N)».
 */
export function duplicatesQuery(tab: TabContext, query: DuplicatesQuery, view: string | null) {
  return queryOptions({
    queryKey: cleanupKeys.duplicates(tabKey(tab), view, query),
    queryFn: ({ signal }) => apiRequest("GET", pagePath("/api/leads/duplicates", query), { schema: duplicatesResponseSchema, tab, signal }),
    staleTime: COUNT_STALE_MS,
  });
}

/** Le pagine delle coppie della scheda «Doppioni»: «Mostra altri» legge la successiva. */
export function duplicatesPagesQuery(tab: TabContext, view: string | null) {
  return infiniteQueryOptions({
    queryKey: cleanupKeys.duplicatesPages(tabKey(tab), view),
    queryFn: ({ signal, pageParam }) =>
      apiRequest("GET", pagePath("/api/leads/duplicates", { offset: pageParam, limit: DUPLICATES_PAGE_SIZE }), { schema: duplicatesResponseSchema, tab, signal }),
    initialPageParam: 0,
    getNextPageParam: (last: DuplicatesResponse) => nextOffset(last, last.groups.length),
    staleTime: PAGES_STALE_MS,
    refetchOnMount: "always",
  });
}

/**
 * La coppia di due Lead per la finestra dell'unione aperta dalla scheda (`GET /api/leads/duplicates/pair`, LEAD-28), letta ogni
 * volta che la finestra si apre: si unisce con le versioni di adesso.
 */
export function duplicatePairQuery(tab: TabContext, view: string | null, first: string, second: string) {
  return queryOptions({
    queryKey: cleanupKeys.pair(tabKey(tab), view, first, second),
    queryFn: ({ signal }) =>
      apiRequest("GET", `/api/leads/duplicates/pair?first=${encodeURIComponent(first)}&second=${encodeURIComponent(second)}`, { schema: duplicateGroupSchema, tab, signal }),
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
  });
}

/** Una pagina delle righe di «Telefoni ed email» (`GET /api/leads/data-issues`); con `limit: 1` soltanto i totali. */
export function dataIssuesQuery(tab: TabContext, query: DataIssuesQuery, view: string | null) {
  return queryOptions({
    queryKey: cleanupKeys.issues(tabKey(tab), view, query),
    queryFn: ({ signal }) => apiRequest("GET", pagePath("/api/leads/data-issues", query), { schema: dataIssuesResponseSchema, tab, signal }),
    staleTime: COUNT_STALE_MS,
  });
}

/** Le pagine delle righe della scheda «Telefoni ed email». */
export function dataIssuesPagesQuery(tab: TabContext, view: string | null) {
  return infiniteQueryOptions({
    queryKey: cleanupKeys.issuesPages(tabKey(tab), view),
    queryFn: ({ signal, pageParam }) =>
      apiRequest("GET", pagePath("/api/leads/data-issues", { offset: pageParam, limit: DATA_ISSUES_PAGE_SIZE }), { schema: dataIssuesResponseSchema, tab, signal }),
    initialPageParam: 0,
    getNextPageParam: (last: DataIssuesResponse) => nextOffset(last, last.issues.length),
    staleTime: PAGES_STALE_MS,
    refetchOnMount: "always",
  });
}

/** Le righe di più pagine, una volta sola anche se una pagina riletta ne ha spostata una in quella dopo. */
export function distinctIssues(pages: ReadonlyArray<{ issues: DataIssue[] }>): DataIssue[] {
  const seen = new Set<string>();
  const rows: DataIssue[] = [];
  for (const row of pages.flatMap((page) => page.issues)) {
    if (seen.has(row.key)) continue;
    seen.add(row.key);
    rows.push(row);
  }
  return rows;
}

/**
 * Tutte le righe con una proposta, per «Correggi tutte le proposte»: quelle già lette se la finestra le ha tutte, altrimenti
 * rilette dall'inizio, pagina per pagina (il numero del pulsante è quello del server, anche delle righe non ancora in vista).
 * `signal` ferma la lettura quando la scheda o la finestra si chiudono. Le pagine sono per posizione: se nel frattempo un
 * collega corregge o tiene una riga, una riga può scivolare fra due pagine e restare fuori (accettato: resta nell'elenco).
 */
export async function allFixableIssues(tab: TabContext, loaded: { rows: readonly DataIssue[]; complete: boolean }, signal?: AbortSignal): Promise<DataIssue[]> {
  if (loaded.complete) return loaded.rows.filter((row) => row.proposal !== null);
  const pages: DataIssuesResponse[] = [];
  let offset: number | undefined = 0;
  while (offset !== undefined) {
    const page: DataIssuesResponse = await apiRequest("GET", pagePath("/api/leads/data-issues", { offset, limit: DATA_ISSUES_PAGE_SIZE }), {
      schema: dataIssuesResponseSchema,
      tab,
      ...(signal ? { signal } : {}),
    });
    pages.push(page);
    offset = nextOffset(page, page.issues.length);
  }
  return distinctIssues(pages).filter((row) => row.proposal !== null);
}

/**
 * Le coppie e le righe di «Pulizia dati» rilette. La promessa finisce quando le letture in vista sono tornate: le scritture la
 * restituiscono, così restano «in corso» (e i pulsanti spenti) finché la coppia unita o la riga corretta non è sparita dalla
 * finestra, e nessuno preme di nuovo su una riga vecchia con la versione di prima.
 */
function rereadCleanup(queryClient: QueryClient, tab: TabKey): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: cleanupKeys.all(tab) });
}

/**
 * Dopo un'unione o una correzione cambiano gli stessi numeri di un cambio dell'archivio (`refreshAfterArchiveChange`: gli
 * elenchi dei Lead, i conteggi del menu delle Smart List e dei filtri, gli archiviati, i Lead per owner), le coppie e le righe
 * di «Pulizia dati» (una correzione può fare o disfare un doppione, un'unione toglie le righe dell'altro Lead), e le schede e
 * le Timeline dei Lead toccati; quella del Lead entrato in un altro non c'è più (404) e non resta in cache. Si aspetta
 * soltanto la rilettura di «Pulizia dati» (`rereadCleanup`); il resto si rilegge dietro la finestra.
 */
function refreshAfterCleanup(queryClient: QueryClient, tab: TabContext, touched: ReadonlySet<string>, gone: ReadonlySet<string> = new Set()): Promise<void> {
  const part = tabKey(tab);
  refreshAfterArchiveChange(queryClient, tab);
  if (touched.size > 0) void queryClient.invalidateQueries(leadCardsFilter(part, touched));
  if (gone.size > 0) queryClient.removeQueries(leadCardsFilter(part, gone));
  return rereadCleanup(queryClient, part);
}

/**
 * «Unisci in un solo Lead» (`POST /api/leads/merge`). Comunque vada si rilegge: riuscita cambia elenchi e coppie; rifiutata
 * (un Lead cambiato nel frattempo, chi resta non è più lo stesso) la coppia va riletta con le versioni di adesso. La scrittura
 * resta in corso finché le coppie non sono rilette (`refreshAfterCleanup`), come le altre di questo file. Dalla scheda del Lead
 * (LEAD-28, `keepGone`) la scheda del Lead entrato nell'altro resta in cache: se è quella aperta, la scheda va prima sul Lead che
 * resta e poi la toglie (`forgetLead`), come dopo «Archivia», così non si rilegge (404) mentre è ancora aperta.
 */
export function useMergeLeads(tab: TabContext, options: { keepGone?: boolean } = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MergeLeadsBody) => apiRequest("POST", "/api/leads/merge", { schema: mergeLeadsResponseSchema, body, tab }),
    onSuccess: (_response, body) => refreshAfterCleanup(queryClient, tab, new Set([body.survivorId]), options.keepGone === true ? new Set() : new Set([body.absorbedId])),
    onError: () => refreshAfterCleanup(queryClient, tab, new Set()),
  });
}

/** Le schede (non le Timeline) dei Lead indicati, in ogni vista. */
function cardsOf(tab: TabKey, leadIds: ReadonlySet<string>) {
  return {
    queryKey: ["scheda", tab, "leads"],
    predicate: (query: { queryKey: QueryKey }) => query.queryKey[3] === "card" && leadIds.has(String(query.queryKey[5])),
  };
}

/**
 * «Non sono doppioni» (`POST /api/leads/duplicates/dismiss`, 204): la coppia non torna più. Si rileggono le coppie e le schede
 * dei due Lead, che dicevano il possibile doppione (LEAD-28).
 */
export function useDismissPair(tab: TabContext) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  return useMutation({
    mutationFn: (body: DismissDuplicateBody) => apiRequest("POST", "/api/leads/duplicates/dismiss", { schema: z.undefined(), body, tab }),
    onSettled: (_result, _error, body) => {
      void queryClient.invalidateQueries(cardsOf(tabPart, new Set(body.leadIds)));
      return rereadCleanup(queryClient, tabPart);
    },
  });
}

/**
 * Le richieste di una correzione di più righe, al più `max` righe ciascuna, senza mai dividere le righe dello stesso Lead (rilievo
 * di Codex sulla #70, turno di notte del 10 ottobre 2026): ogni correzione alza la versione del Lead, e una seconda richiesta con
 * la versione di prima tornerebbe con `version_conflict`. I Lead nell'ordine in cui compaiono; le loro righe insieme.
 */
export function fixBatchesOf<Row extends { leadId: string }>(rows: readonly Row[], max: number): Row[][] {
  const byLead = new Map<string, Row[]>();
  for (const row of rows) {
    const group = byLead.get(row.leadId);
    if (group) group.push(row);
    else byLead.set(row.leadId, [row]);
  }
  const batches: Row[][] = [];
  let current: Row[] = [];
  for (const group of byLead.values()) {
    if (current.length > 0 && current.length + group.length > max) {
      batches.push(current);
      current = [];
    }
    current.push(...group);
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

/** Com'è andata una correzione di più righe; `stoppedBy` è il rifiuto che ha fermato le richieste rimaste (`unsent` righe). */
export type FixOutcome = FixDataIssuesResponse & { stoppedBy: unknown; unsent: number };

/**
 * «Correggi» e «Correggi tutte le proposte» (`POST /api/leads/data-issues/fix`): al massimo `DATA_ISSUE_FIX_MAX` righe per
 * richiesta, una richiesta dopo l'altra. Il rifiuto della prima è l'errore della correzione; quello di una richiesta dopo
 * ferma le altre e resta nell'esito, con le righe già corrette. Poi si rileggono elenchi, righe, schede e Timeline dei Lead.
 */
export function useFixIssues(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (rows: readonly DataIssue[]): Promise<FixOutcome> => {
      const outcome: FixOutcome = { fixed: 0, skipped: [], stoppedBy: null, unsent: 0 };
      let sent = 0;
      for (const chunk of fixBatchesOf(rows, DATA_ISSUE_FIX_MAX)) {
        const body: FixDataIssuesBody = { fixes: chunk.map((row) => ({ key: row.key, leadId: row.leadId, version: row.version })) };
        try {
          const response = await apiRequest("POST", "/api/leads/data-issues/fix", { schema: fixDataIssuesResponseSchema, body, tab });
          outcome.fixed += response.fixed;
          outcome.skipped.push(...response.skipped);
          sent += chunk.length;
        } catch (error) {
          if (sent === 0) throw error;
          outcome.stoppedBy = error;
          outcome.unsent = rows.length - sent;
          break;
        }
      }
      return outcome;
    },
    onSettled: (_outcome, _error, rows) => refreshAfterCleanup(queryClient, tab, new Set(rows.map((row) => row.leadId))),
  });
}

/** «Tieni così» (`POST /api/leads/data-issues/dismiss`, 204): quella riga non torna finché il valore non cambia. */
export function useDismissIssue(tab: TabContext) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  return useMutation({
    mutationFn: (body: DismissDataIssueBody) => apiRequest("POST", "/api/leads/data-issues/dismiss", { schema: z.undefined(), body, tab }),
    onSettled: () => rereadCleanup(queryClient, tabPart),
  });
}
